<template>
  <Dialog
    v-model:visible="modalVisible"
    class="popup form-sequence-modal"
    modal
    :close-on-escape="false"
    :closable="false"
    @after-hide="handleModalClose"
  >
    <template #header>
      <slot name="header" v-bind="{ title, titleIcon }">
        <strong>
          <font-awesome-icon v-if="titleIcon" :icon="titleIcon" />
          {{ title }}
        </strong>
      </slot>
      <button type="button" class="btn btn-light" @click="closeConfirm">
        <font-awesome-icon icon="times" />
      </button>
    </template>
    <slot></slot>
    <div class="d-flex flex-column h-100">
      <div class="flex-grow-1 component-wrapper">
        <component
          :is="fs.resolvedStep.component"
          v-bind="fs.resolvedStep.props"
          :key="fs.resolvedStep.id"
          :answer="fs.answers.get(fs.resolvedStep.id)"
          :answers="fs.answers"
          :lookup-step-title="lookupStepTitle"
          @step-validity-updated="handlestepValidityUpdated"
          @modal-options-updated="handleModalOptionsUpdated"
          @vue:mounted="handleComponentMounted"
          @vue:unmounted="handleComponentUnmounted"
        />
      </div>

      <p
        v-if="fs.resolvedStep?.final && fs.nextResult?.success"
        class="text-success mb-0 mt-2 p-0 d-flex justify-content-center align-items-center"
      >
        <font-awesome-icon icon="check-circle" fixed-width class="me-2" />
        Submitted
      </p>
    </div>

    <template #footer>
      <div v-show="!hideFooter">
        <div class="d-flex align-items-center justify-content-center">
          <div class="me-3">
            <button
              class="btn btn-outline-secondary"
              :disabled="
                fs.history.length === 0 ||
                fs.loading ||
                fs.nextResult?.success ||
                componentIsLoading
              "
              @click="fs.goBack"
            >
              Back
            </button>
          </div>
          <div>
            <button
              v-if="!fs.nextResult?.success"
              class="btn btn-primary"
              :disabled="
                !stepValidityUpdated || fs.loading || componentIsLoading
              "
              @click="onNextClicked"
            >
              <font-awesome-icon
                v-if="fs.loading"
                icon="spinner"
                spin
                fixed-width
              />
              {{ fs.resolvedStep.final ? "Submit" : "Next" }}
            </button>
            <button v-else class="btn btn-primary" @click="fs.resetState">
              <font-awesome-icon icon="redo" fixed-width />
              Start again
            </button>
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, onErrorCaptured } from "vue";
import useFormSequenceModal from "@/components/form/useFormSequenceModal";
import find from "lodash/find";
import get from "lodash/get";
import Dialog from "primevue/dialog";

const stepValidityUpdated = ref(false);
const validAnswer = ref(null);
const hideFooter = ref(false);
const componentIsLoading = ref(false);

onErrorCaptured((_error, instance, info) => {
  console.error(
    `Error in component ${instance?.type?.name || "unknown"}: ${info}`,
    _error,
  );
  return true; // propogate the error
});

const { modalVisible, title, titleIcon, fs, fsDefinition } =
  useFormSequenceModal();

const handlestepValidityUpdated = (isValid, answer) => {
  stepValidityUpdated.value = isValid;
  if (isValid) {
    validAnswer.value = answer;
  }
};

const handleModalOptionsUpdated = async (opt) => {
  if (opt.hideFooter !== undefined) {
    hideFooter.value = opt.hideFooter;
  }
  if (opt.nextClick !== undefined) {
    await fs.value.submitAnswer(opt.nextClick);
  }
};

const handleComponentMounted = () => {
  componentIsLoading.value = false;
};
const handleComponentUnmounted = () => {
  componentIsLoading.value = true;
};

const onNextClicked = async () => {
  await fs.value.submitAnswer(validAnswer.value);
};

const handleModalClose = () => {
  fs.value.resetState();
};

const closeConfirm = () => {
  // allow closing the form if the current step is the first step
  if (fs.value.resolvedStep.id == fsDefinition.value[0].id) {
    modalVisible.value = false;
    return;
  }
  // plain javascript confirm, since we may already be in modal window hell
  if (confirm("Are you sure you want to close the form?")) {
    modalVisible.value = false;
  }
};

const lookupStepTitle = (id) => {
  return get(find(fsDefinition.value, { id }), "props.title");
};
</script>

<style>
/* hide the close button container */
.form-sequence-modal div[data-pc-section="icons"] {
  display: none;
}
</style>

<style scoped>
.component-wrapper {
  min-height: 16em;
}
div.modal-footer {
  padding: 0.3rem;
}
</style>
